import { svgIcons } from '@/assets/svg/icons';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import ScreenerResult from '@/components/molecules/ScreenerResult';
import CircleFillOutWithText from '@/components/molecules/WellnessStateCircle/CircleFillOutWithText';
import useScreenerById from '@/hooks/assessment/useScreenerById';
import { TMemberCareMember } from '@/types/MemberCare.types';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';
import { HStack, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import React from 'react';

const IMAGE_SIZE_PX = '80px';

type Props = {
  memberId: string;
  member: TMemberCareMember;
  onClose: () => void;
  screenerId?: string;
  isOpen: boolean;
};

const ScreenerResultModal = ({ isOpen, onClose, member, screenerId }: Props) => {
  const { isLoading, data } = useScreenerById(screenerId);

  const [totalScoreX10, setTotalScoreX10] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (data?.totalScore !== undefined) {
      setTotalScoreX10(data.totalScore > 10 ? data.totalScore : data.totalScore * 10);
    }
  }, [data]);

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      width="1240px"
      modalBodyPadding={0}
      headerContent={
        <HStack width={'full'} justifyContent={'space-between'}>
          <HStack width={'full'} gap={'20px'}>
            <Image
              src={member.profilePicture || svgIcons.genericUser}
              width={IMAGE_SIZE_PX}
              height={IMAGE_SIZE_PX}
              alt="member-profile-photo"
              borderRadius={'50%'}
              objectFit={'cover'}
            />
            <VStack alignItems={'flex-start'}>
              <Text variant="loraSmallTitle" fontWeight={'500'}>
                {member.firstName} {member.lastName}
              </Text>
              <Text
                variant={'urbanistSmall'}
                fontWeight={'800'}
                color={'text.mediumGray'}
              >{`SCREENER DATE & TIME`}</Text>
              {data?.createdAt && (
                <Text variant={'urbanistSmall'} textColor={'text.mediumBlue'}>
                  {new Date(data?.createdAt).toLocaleString()}
                </Text>
              )}
              {isLoading && <Spinner size={'sm'} />}
            </VStack>
            {totalScoreX10 && (
              <HStack marginLeft={'60px'} alignItems={'center'} gap={'20px'}>
                <CircleFillOutWithText
                  circleDiameter={80}
                  svgId={`screener-${data?._id}`}
                  thickness={7}
                  percentageTextStyle={{ fontSize: '17px', fontWeight: '600' }}
                  percentageFilled={totalScoreX10}
                  circleColor={getColorFromPercentage(totalScoreX10)}
                  boxShadow={'0px 3px 6px 0px #0E006224'}
                />
                <VStack alignItems={'flex-start'} gap={'2px'}>
                  <Text variant={'urbanistSmall'} fontWeight={'800'} color={'text.mediumGray'}>
                    COMBINED
                  </Text>
                  <Text variant={'urbanistSmall'} fontWeight={'800'} color={'text.mediumGray'}>
                    WHOLENESS
                  </Text>
                  <Text variant={'urbanistSmall'} fontWeight={'800'} color={'text.mediumGray'}>
                    SCORE
                  </Text>
                </VStack>
              </HStack>
            )}
          </HStack>
        </HStack>
      }
    >
      <VStack minHeight={'404px'} width={'full'} marginTop={'40px'}>
        {!isLoading && data && (
          <VStack alignItems={'flex-start'} width={'full'} height={'full'}>
            <ScreenerResult screener={data} />
          </VStack>
        )}
        {isLoading && (
          <VStack alignItems={'center'} justifyContent={'center'} width={'full'} height={'full'}>
            <LoadingLogo />
          </VStack>
        )}
      </VStack>
    </ModalContainer>
  );
};

export default ScreenerResultModal;
